import { useMemo } from 'react';

import {
  DEFAULT_PAGE_NAMES,
  type PageIdentifier,
} from 'hb-react/shared/constants/pageIdentifiers';
import { usePageNamesQuery } from 'hb-react/shared/hooks/usePageNamesQuery';

import {
  useTopicId,
  useGlobalOrTopicMenu,
  usePageNamesFromNetworkConfiguration,
} from './hooks';
import { type UsePageMenuNamesOptions } from './types';
import {
  fillUnknownWithEllipsisStrings,
  fillUnknownWithDefaultNames,
  getPageNamesFromMenu,
} from './utils';

/**
 * Hook that returns the names of the pages from the menu. It first tries to
 * get the names from the cached menu, and if they are not found, it fetches
 * them from the API endpoint.
 *
 * @param identifiers {Array<PageIdentifier>} The list of page identifiers.
 * @param options {UsePageMenuNamesOptions} Options object.
 * @returns {PageNames} The names of the pages.
 */
export const usePageMenuNames = (
  identifiers: Array<PageIdentifier>,
  {
    topicId: topicIdParam,
    defaultNames = DEFAULT_PAGE_NAMES,
  }: UsePageMenuNamesOptions = {},
) => {
  const topicId = useTopicId(topicIdParam);
  const menu = useGlobalOrTopicMenu(topicId);

  // Some page names might be available from the network configuration.
  const pageNamesFromNetworkConfiguration =
    usePageNamesFromNetworkConfiguration(identifiers);

  // Get the names of the pages from the menu.
  const { pageNames, notFound } = getPageNamesFromMenu({
    availableNames: pageNamesFromNetworkConfiguration,
    menu,
    identifiers,
    isTopic: Boolean(topicId),
  });

  // Fetch missing names of the pages from the API endpoint.
  const { isLoading, data: fetchedPageNames } = usePageNamesQuery(notFound);

  return useMemo(() => {
    if (!identifiers.length) {
      return {};
    }

    // Ellipsis strings for unknown pages while loading.
    if (isLoading) {
      return fillUnknownWithEllipsisStrings(pageNames);
    }

    const consolidatedNames = {
      ...pageNames,
      ...fetchedPageNames,
    };

    // If any page is still missing, use the default name.
    return fillUnknownWithDefaultNames(consolidatedNames, defaultNames);
  }, [
    identifiers.length,
    isLoading,
    pageNames,
    fetchedPageNames,
    defaultNames,
  ]);
};

// Re-export the page identifiers for convenience.
export { PAGES } from 'hb-react/shared/constants/pageIdentifiers';
